import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Resolver, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Button from "../../components/ui/Button";
import Icon from "../../components/ui/Icon";
import { UserRole } from "../../model/UserRole";
import { useTitle } from "../../components/TitleContext";
import { UserFormInputs } from "../../hooks/user/useAddUser";

const createUserSchema = (isEditMode: boolean) =>
  yup.object().shape({
    username: yup.string().required("Username is required."),
    email: yup.string().email().required("Email is required."),
    password: isEditMode
      ? yup
          .string()
          .transform((value) => (value === "" ? undefined : value))
          .nullable()
          .notRequired()
          .min(8, "Password must be at least 8 characters long.")
      : yup.string().min(8, "Password must be at least 8 characters long.").required("Password is required."),
    confirmPassword: isEditMode
      ? yup
          .string()
          .transform((value) => (value === "" ? undefined : value))
          .nullable()
          .when("password", {
            is: (password: string | undefined) => password && password.length > 0,
            then: (schema) =>
              schema.required("Please confirm your password.").oneOf([yup.ref("password")], "Passwords must match."),
            otherwise: (schema) => schema.notRequired(),
          })
      : yup
          .string()
          .min(8, "Password must be at least 8 characters long.")
          .required("Password is required.")
          .when("password", {
            is: (password: string | undefined) => password && password.length > 0,
            then: (schema) =>
              schema.required("Please confirm your password.").oneOf([yup.ref("password")], "Passwords must match."),
            otherwise: (schema) => schema.notRequired(),
          }),
    roles: yup.array().of(yup.string()).min(1, "Please select a role.").required("Please select a role."),
  });
interface UserFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: UserFormInputs) => void;
  defaultValues?: Partial<UserFormInputs>;
}

const UserForm: React.FC<UserFormProps> = ({ isOpen, onClose, onSubmit, defaultValues }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { setSubtitle } = useTitle();
  const isEditMode = useMemo(() => !!defaultValues?.username, [defaultValues?.username]);
  const schema = useMemo(() => createUserSchema(isEditMode), [isEditMode]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<UserFormInputs>({
    resolver: yupResolver(schema) as Resolver<UserFormInputs>,
    defaultValues,
  });

  const passwordValue = watch("password");

  const handleFormSubmit = useCallback(
    (data: UserFormInputs) => {
      const submitData = { ...data };

      if (isEditMode && (!data.password || data.password.trim() === "")) {
        delete submitData.password;
        delete submitData.confirmPassword;
      }

      onSubmit(submitData);
    },
    [isEditMode, onSubmit]
  );

  const handleClose = useCallback(() => {
    setSubtitle("");
    onClose();
  }, [setSubtitle, onClose]);

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const toggleConfirmPasswordVisibility = useCallback(() => {
    setShowConfirmPassword((prev) => !prev);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleClose();
      }
    };

    if (isOpen) {
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }
  }, [isOpen, handleClose]);

  useEffect(() => {
    if (isOpen && defaultValues?.username) {
      setSubtitle(defaultValues.username);
    }
    return () => {
      if (!isOpen) {
        setSubtitle("");
      }
    };
  }, [isOpen, defaultValues?.username, setSubtitle]);

  const roleEntries = useMemo(() => Object.entries(UserRole), []);
  const formTitle = useMemo(() => {
    return defaultValues?.username ? `Update User : ${defaultValues.username}` : "Add User";
  }, [defaultValues?.username]);

  const buttonText = useMemo(() => {
    return defaultValues?.username ? "Update" : "Add";
  }, [defaultValues?.username]);

  if (!isOpen) return null;

  return (
    <div className="fixed top-0 right-0 left-0 z-50 flex justify-center items-center w-full h-full bg-gray-900 bg-opacity-50 overflow-hidden">
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md relative">
        <button
          className="absolute top-0 right-0 px-2 py-1 rounded font-display font-semibold focus:outline-none hover:bg-primary"
          onClick={handleClose}
          type="button"
          aria-label="Close dialog"
        >
          <Icon icon="close" hoveredVariant="light" />
        </button>

        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold font-display py-2">{formTitle}</h2>
        </div>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="font-display">
          {/* Username Field */}
          <div className="mb-4">
            <label htmlFor="username" className="block text-sm font-medium text-gray-900">
              Username :
            </label>
            <input
              id="username"
              type="text"
              className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              {...register("username")}
              autoComplete="username"
            />
            {errors.username && <p className="text-red-500 text-xs mt-1">{errors.username.message}</p>}
          </div>

          {/* Email Field */}
          <div className="mb-4">
            <label htmlFor="email" className="block text-sm font-medium text-gray-900">
              Email :
            </label>
            <input
              id="email"
              type="email"
              className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              {...register("email")}
              disabled={!!defaultValues?.email}
              autoComplete="email"
            />
            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
          </div>

          {/* Password Field */}
          <div className="mb-4">
            <label htmlFor="password" className="block text-sm font-medium text-gray-900">
              Password {isEditMode ? " (leave blank to keep current)" : ""} :
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 pr-10"
                {...register("password")}
                autoComplete={isEditMode ? "new-password" : "current-password"}
                placeholder={isEditMode ? "Enter new password or leave blank" : ""}
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility()}
                className="absolute inset-y-0 right-0 flex items-center px-3"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                <Icon icon={showPassword ? "eyeSlash" : "eye"} />
              </button>
            </div>
            {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>}
          </div>

          {/* Confirm Password Field */}
          <div className="mb-4">
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-900">
              Confirm password :
            </label>
            <div className="relative">
              <input
                id="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                className={`mt-1 block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 pr-10 ${
                  isEditMode && !passwordValue ? "bg-gray-100" : ""
                }`}
                {...register("confirmPassword")}
                disabled={isEditMode && !passwordValue}
                placeholder={isEditMode && !passwordValue ? "Enter password above first" : ""}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => toggleConfirmPasswordVisibility()}
                className="absolute inset-y-0 right-0 flex items-center px-3"
                aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
              >
                <Icon icon={showConfirmPassword ? "eyeSlash" : "eye"} />
              </button>
            </div>
            {errors.confirmPassword && <p className="text-red-500 text-xs mt-1">{errors.confirmPassword.message}</p>}
          </div>

          {/* Roles Field */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Roles : <span className="text-gray-500 text-xs">(Select one or more)</span>
            </label>
            <div className="space-y-2 border border-gray-300 rounded-lg p-3 bg-gray-50">
              {roleEntries.map(([key, value]) => (
                <label key={key} className="flex items-center cursor-pointer hover:bg-gray-100 p-1 rounded">
                  <input
                    type="checkbox"
                    value={key}
                    {...register("roles")}
                    className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-900">{value}</span>
                </label>
              ))}
            </div>
            {errors.roles && <p className="text-red-500 text-xs mt-1">{errors.roles.message}</p>}
          </div>

          <div className="flex items-center">
            <Button variant="secondary" type="submit" width="w-full">
              {buttonText}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserForm;
